import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import Spinner from "../../../shared/components/ui/Spinner";
import BarChart from "./BarChart";

const STORAGE_KEY = "ai_quality_advanced_expanded";

function getApprovalColor(rate) {
  if (rate === null || rate === undefined) return "total";
  if (rate >= 70) return "done";
  if (rate >= 50) return "warning";
  return "error";
}

function humanizeFeatureName(name) {
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ModelQualityTab({ aiQualityStats, showRain }) {
  const cardsRef = useRef([]);
  const animDone = useRef(false);
  const [advancedExpanded, setAdvancedExpanded] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "true";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, advancedExpanded);
  }, [advancedExpanded]);

  useEffect(() => {
    if (aiQualityStats && cardsRef.current.length && !animDone.current) {
      gsap.fromTo(
        cardsRef.current.filter(Boolean),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.4, stagger: 0.05, ease: "power2.out" }
      );
      animDone.current = true;
    }
  }, [aiQualityStats]);

  if (!aiQualityStats) {
    return (
      <div className="stats-loading-section" aria-live="polite">
        <Spinner size="md" />
        Loading AI quality statistics...
      </div>
    );
  }

  const {
    crop_quality,
    gaic,
    relevance,
    selection_reasons,
    scene_types,
    quality_scores,
    sample_count,
    crop_learning,
  } = aiQualityStats;

  // Format GAIC histogram for BarChart
  const gaicHistogramData =
    gaic?.histogram?.map((item) => ({
      name: item.range,
      count: item.count,
    })) || [];

  // Format crop method distribution for BarChart
  const cropMethodData = crop_quality?.method_distribution || [];

  // Format scene types for BarChart
  const sceneTypeData = scene_types || [];

  // Check if there's any data
  const hasGaicData = gaic?.sample_count > 0;
  const hasCropData = crop_quality?.method_distribution?.length > 0;
  const hasRelevanceData = relevance?.avg_importance !== null;
  const hasSceneData = scene_types?.length > 0;
  const hasQualityData =
    quality_scores?.blur_avg !== null ||
    quality_scores?.exposure_avg !== null ||
    quality_scores?.composition_avg !== null;

  // Crop learning stats
  const totalFeedback = crop_learning?.total_feedback || 0;
  const recrops = crop_learning?.recrops || 0;
  const approvals = crop_learning?.approvals || 0;
  const oracleStatus = crop_learning?.oracle_status;
  const modelTrained = oracleStatus?.model_trained || false;
  // Use DB feedback count (persistent) as progress indicator, not model.n_samples
  // which stays 0 until training triggers at 50+ samples
  const modelSamples = Math.max(oracleStatus?.total_samples || 0, totalFeedback);
  const trainingThreshold = 50;
  const topWeights = oracleStatus?.top_weights || {};

  // Approval rate (only meaningful with enough samples)
  const approvalRate = totalFeedback >= 5 ? (approvals / totalFeedback) * 100 : null;

  // Check if there's anything to show at all
  const hasAnyData =
    hasGaicData || hasCropData || hasRelevanceData || hasSceneData || hasQualityData ||
    (sample_count && sample_count > 0) || totalFeedback > 0;

  if (!hasAnyData) {
    return (
      <div className="stats-subsection">
        <div className="no-data">
          No AI quality data available. Process some images through the pipeline to see statistics.
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Summary Cards */}
      <div className="stat-cards-grid">
        <div
          className="stat-card info"
          ref={(el) => (cardsRef.current[0] = el)}
          aria-label={`Average Crop Score: ${crop_quality?.avg_score?.toFixed(3) || 'N/A'}`}
        >
          <span className="stat-value">{crop_quality?.avg_score?.toFixed(3) || '-'}</span>
          <span className="stat-label">Avg Crop Score</span>
        </div>
        <div
          className="stat-card processing"
          ref={(el) => (cardsRef.current[1] = el)}
          aria-label={`Zero Violation Rate: ${((crop_quality?.zero_violation_rate || 0) * 100).toFixed(1)}%`}
        >
          <span className="stat-value">
            {((crop_quality?.zero_violation_rate || 0) * 100).toFixed(1)}%
          </span>
          <span className="stat-label">Zero-Violation Rate</span>
        </div>
        <div
          className={`stat-card ${getApprovalColor(approvalRate)}`}
          ref={(el) => (cardsRef.current[2] = el)}
          aria-label={`AI Approval Rate: ${approvalRate !== null ? approvalRate.toFixed(1) + '%' : 'N/A'}`}
        >
          <span className="stat-value">
            {approvalRate !== null ? `${approvalRate.toFixed(1)}%` : '--'}
          </span>
          <span className="stat-label">AI Approval Rate</span>
        </div>
        <div
          className="stat-card total"
          ref={(el) => (cardsRef.current[3] = el)}
          aria-label={`Crop Feedback: ${totalFeedback}`}
        >
          <span className="stat-value">{totalFeedback}</span>
          <span className="stat-label">Crop Feedback</span>
        </div>
      </div>

      {/* Two-column chart grid */}
      <div className="ai-quality-charts-grid">
        <div className="chart-section">
          {hasGaicData ? (
            <>
              <BarChart
                data={gaicHistogramData}
                title="GAIC Score Distribution"
                color="#66ffb7"
                maxItems={5}
                showRain={showRain}
              />
              <p className="stats-hint" aria-label="GAIC score range">
                P10: {gaic?.p10?.toFixed(3) || '-'} | Median:{' '}
                {gaic?.median?.toFixed(3) || '-'} | P90: {gaic?.p90?.toFixed(3) || '-'}
              </p>
            </>
          ) : (
            <>
              <h4>GAIC Score Distribution</h4>
              <p className="no-data">
                GAIC neural aesthetic scoring is not active. Crop quality is evaluated using
                composition rules and learned preferences.
              </p>
            </>
          )}
        </div>
        <div className="chart-section">
          <BarChart
            data={cropMethodData}
            title="Crop Method Distribution"
            color="#a29bff"
            maxItems={10}
            showRain={showRain}
          />
        </div>
      </div>

      {/* Crop Learning Section */}
      <div className="stats-subsection">
        <h3>Crop Learning</h3>

        <div className="stat-cards-grid small">
          <div
            className="stat-card done"
            ref={(el) => (cardsRef.current[4] = el)}
            aria-label={`AI Approved: ${approvals}`}
          >
            <span className="stat-value">{approvals}</span>
            <span className="stat-label">AI Approved</span>
          </div>
          <div
            className="stat-card warning"
            ref={(el) => (cardsRef.current[5] = el)}
            aria-label={`Manual Recrops: ${recrops}`}
          >
            <span className="stat-value">{recrops}</span>
            <span className="stat-label">Manual Recrops</span>
          </div>
          <div
            className="stat-card total"
            ref={(el) => (cardsRef.current[6] = el)}
            aria-label={`Total Decisions: ${totalFeedback}`}
          >
            <span className="stat-value">{totalFeedback}</span>
            <span className="stat-label">Total Decisions</span>
          </div>
        </div>

        <div className="crop-learning-oracle">
          <h4>
            Oracle Status: {modelTrained ? 'Model Active' : 'Collecting'}
            {!modelTrained && ` (${modelSamples}/${trainingThreshold})`}
          </h4>
          <div
            role="progressbar"
            aria-valuenow={Math.min(modelSamples, trainingThreshold)}
            aria-valuemin={0}
            aria-valuemax={trainingThreshold}
            style={{
              background: 'var(--bg-tertiary, #1a1a2e)',
              borderRadius: '4px',
              height: '8px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min((modelSamples / trainingThreshold) * 100, 100)}%`,
                height: '100%',
                background: modelTrained ? '#66ffb7' : '#ffd666',
                borderRadius: '4px',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <p className="stats-hint">
            {modelTrained
              ? `Trained on ${modelSamples} samples with ${oracleStatus?.n_features || 0} features.`
              : `${Math.max(0, trainingThreshold - modelSamples)} more crop decisions needed. Keep editing to teach the AI your preferences.`}
          </p>
        </div>

        {modelTrained && Object.keys(topWeights).length > 0 && (
          <div className="crop-learning-oracle">
            <h4>Learned Preferences</h4>
            <div className="weight-bars">
              {Object.entries(topWeights).map(([feature, weight]) => {
                const maxWeight = Math.max(
                  ...Object.values(topWeights).map((w) => Math.abs(w))
                );
                const pct = maxWeight > 0 ? (Math.abs(weight) / maxWeight) * 100 : 0;
                return (
                  <div
                    key={feature}
                    className="weight-row"
                    aria-label={`${humanizeFeatureName(feature)}: ${weight.toFixed(3)}`}
                  >
                    <div className="weight-label">{humanizeFeatureName(feature)}</div>
                    <div className="weight-bar-container">
                      <div
                        className={`weight-bar ${weight >= 0 ? 'positive' : 'negative'}`}
                        style={{
                          width: `${pct}%`,
                          background: showRain
                            ? `linear-gradient(90deg, ${weight >= 0 ? '#66ffb7' : '#ff6b6b'}, ${weight >= 0 ? '#66ffb788' : '#ff6b6b88'})`
                            : weight >= 0
                              ? '#66ffb7'
                              : '#ff6b6b',
                        }}
                      />
                    </div>
                    <div className="weight-value">{weight.toFixed(3)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Collapsible Advanced Breakdown */}
      <div className="stats-subsection">
        <button
          className="advanced-toggle"
          onClick={() => setAdvancedExpanded(!advancedExpanded)}
          aria-expanded={advancedExpanded}
          aria-controls="advanced-breakdown"
        >
          <h3>Advanced Breakdown</h3>
          {advancedExpanded ? (
            <ChevronUpIcon className="toggle-icon" />
          ) : (
            <ChevronDownIcon className="toggle-icon" />
          )}
        </button>

        {advancedExpanded && (
          <div id="advanced-breakdown" className="advanced-breakdown-content">
            {/* Relevance factors and scene types */}
            <div className="ai-quality-charts-grid">
              {/* Relevance Factor Scores */}
              {hasRelevanceData && (
                <div className="chart-section">
                  <h4>Relevance Factor Averages</h4>
                  <p className="stats-hint">
                    Average importance score: {relevance?.avg_importance?.toFixed(3) || '-'}
                  </p>
                  <div className="weight-bars">
                    {Object.entries(relevance?.factor_averages || {}).map(([factor, value]) => {
                      const maxValue = Math.max(
                        ...Object.values(relevance?.factor_averages || {}).filter(
                          (v) => v !== null
                        )
                      );
                      const pct = value !== null && maxValue > 0 ? (value / maxValue) * 100 : 0;
                      const label =
                        factor.charAt(0).toUpperCase() + factor.slice(1).replace(/_/g, ' ');
                      return (
                        <div
                          key={factor}
                          className="weight-row"
                          aria-label={`${label}: ${value?.toFixed(3) || 'N/A'}`}
                        >
                          <div className="weight-label">{label}</div>
                          <div className="weight-bar-container">
                            <div
                              className="weight-bar positive"
                              style={{
                                width: `${pct}%`,
                                background: showRain
                                  ? 'linear-gradient(90deg, #8ce5ff, #8ce5ff88)'
                                  : '#8ce5ff',
                              }}
                            />
                          </div>
                          <div className="weight-value">{value?.toFixed(3) || '-'}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Scene Type Distribution */}
              <div className="chart-section">
                <BarChart
                  data={sceneTypeData}
                  title="Scene Type Distribution"
                  color="#ff9b64"
                  maxItems={5}
                  showRain={showRain}
                />
              </div>
            </div>

            {/* Quality Metrics */}
            {hasQualityData && (
              <div className="quality-metrics-section">
                <h4>Quality Metrics</h4>
                <div className="stat-cards-grid small">
                  <div className="stat-card" ref={(el) => (cardsRef.current[7] = el)}>
                    <span className="stat-value">
                      {quality_scores?.blur_avg?.toFixed(3) || '-'}
                    </span>
                    <span className="stat-label">Blur Avg</span>
                  </div>
                  <div className="stat-card" ref={(el) => (cardsRef.current[8] = el)}>
                    <span className="stat-value">
                      {quality_scores?.exposure_avg?.toFixed(3) || '-'}
                    </span>
                    <span className="stat-label">Exposure Avg</span>
                  </div>
                  <div className="stat-card" ref={(el) => (cardsRef.current[9] = el)}>
                    <span className="stat-value">
                      {quality_scores?.composition_avg?.toFixed(3) || '-'}
                    </span>
                    <span className="stat-label">Composition Avg</span>
                  </div>
                </div>
              </div>
            )}

            {/* Selection Reasons */}
            {selection_reasons?.length > 0 && (
              <div className="chart-section">
                <BarChart
                  data={selection_reasons}
                  title="Selection Reasons"
                  color="#b8e986"
                  maxItems={10}
                  showRain={showRain}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
